import { JSX, useCallback, useContext, useEffect, useState } from 'react';
import { SearchFilterContentTypes, SearchOrigins } from 'search-gateway-client';

import {
    NavSearchContext,
    NavSearchContextProvider,
    NavSearchProps,
    StatsigExperimentContext,
} from '../context/NavSearchContext';
import { CrossAssetNavSuggestions } from '../types/shared';
import useMedia from '../utils/useMedia';
import { saveSearchHistory } from './AutosuggestElements/utils';
import CrossAssetNavSearchBox from './NavSearchElements/CrossAssetNavSearchBox';
import NavSearchBox from './NavSearchElements/NavSearchBox';
import NavSearchButton from './NavSearchElements/NavSearchButton';
import NavSearchDropdown from './NavSearchElements/NavSearchDropdown';
import { cleanSearchKeywords } from './NavSearchElements/searchUtils';

export default function NavSearch(props: NavSearchProps): JSX.Element {
    const {
        onSubmit,
        userHasAnySubscription,
        useSnapshots,
        searchOrigin: searchOriginFromProps = SearchOrigins.TOP_NAV,
    } = props;
    const [contentType, setContentType] = useState(props.contentType);
    const [searchTerm, setSearchTerm] = useState('');
    const context = useContext(StatsigExperimentContext);

    const makeEmptySuggestions = (): CrossAssetNavSuggestions => ({
        keywords: [],
        relatedCollections: [],
        latestCollections: [],
        trendingCollections: [],
        relatedContentTypesAndKeywords: [],
    });

    const [suggestions, setSuggestions] = useState<CrossAssetNavSuggestions>(makeEmptySuggestions());

    const clearSuggestions = useCallback(() => setSuggestions(makeEmptySuggestions()), []);

    // i.e. tailwind md breakpoint https://storywind.netlify.app/?path=/story/design-grids--page#medium-672-1024px
    const isMediumScreen = useMedia('(min-width: 672px)', true);
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setContentType(props.contentType);
    }, [props.contentType]);

    const handleDropdownChange = (contentType: string) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        setContentType(contentType as SearchFilterContentTypes);
    };
    const handleSubmit = (
        contentTypeSearched: SearchFilterContentTypes = contentType,
        value: string = searchTerm,
        searchOriginOverride?: SearchOrigins,
    ) => {
        const cleanedSearchKeywords = cleanSearchKeywords(value);
        const searchOrigin = searchOriginOverride ?? searchOriginFromProps;
        if (context?.isSearchHistoryEnabled) {
            saveSearchHistory(contentTypeSearched, cleanedSearchKeywords);
        }
        onSubmit(contentTypeSearched, cleanedSearchKeywords, searchOrigin);
    };

    return (
        <NavSearchContextProvider {...props}>
            <NavSearchContext.Consumer>
                {({ setShowSuggestions }) => (
                    <div className="flex flex-col xl:w-full lg:w-72 font-inter">
                        <form
                            className="w-full h-10 px-4 py-2 bg-white rounded-3xl border border-solid border-gray-500 justify-between items-center inline-flex"
                            onSubmit={(event) => {
                                event.preventDefault();
                                setShowSuggestions(false);
                                handleSubmit(contentType, searchTerm);
                            }}
                        >
                            <div className="w-full h-6 justify-start items-center gap-0.5 inline-flex">
                                <NavSearchDropdown
                                    onChange={handleDropdownChange}
                                    value={contentType}
                                    useSnapshots={useSnapshots}
                                />
                                <div className="xl:w-px lg:w-px md:w-px h-6 bg-gray-500" />
                                {isMediumScreen ? (
                                    <CrossAssetNavSearchBox
                                        contentType={contentType}
                                        setSearchTerm={setSearchTerm}
                                        onSubmit={handleSubmit}
                                        searchTerm={searchTerm}
                                        suggestions={suggestions}
                                        setSuggestions={setSuggestions}
                                        clearSuggestions={clearSuggestions}
                                    />
                                ) : (
                                    <NavSearchBox
                                        contentType={contentType}
                                        onChange={setSearchTerm}
                                        onSubmit={handleSubmit}
                                        searchTerm={searchTerm}
                                        userHasAnySubscription={userHasAnySubscription}
                                    />
                                )}
                            </div>
                            <NavSearchButton />
                        </form>
                    </div>
                )}
            </NavSearchContext.Consumer>
        </NavSearchContextProvider>
    );
}
